import React from 'react';
import PropTypes from 'prop-types';
import equal from 'fast-deep-equal';
import {formatDate} from 'common/formatters/date-formatter';
import {DateTimeFormat} from 'common/proxies/enum-proxy';
import SvgText from 'components/svg-text';
import TooltipBackground from 'components/tooltip-background';
import TooltipItem from 'components/tooltip-item';
import {truncate} from 'common/utils/string-util';
import {getTranslate} from 'utils/attribute-util';
import {isValid} from 'validators/number-validator';
import styles from './index.css';

export const TooltipContext = React.createContext({
  getContainerEdges: () => ({}),
});

export default class Tooltip extends React.Component {
  static propTypes = {
    activeIndex: PropTypes.number,
    anchorLength: PropTypes.number,
    getContainerEdges: PropTypes.func,
    itemHeight: PropTypes.number,
    padding: PropTypes.number,
    series: PropTypes.array,
    x: PropTypes.number,
    y: PropTypes.number,
  };

  static defaultProps = {
    activeIndex: -1,
    anchorLength: 12,
    itemHeight: 20,
    padding: 16,
    series: [],
    x: 0,
    y: 0,
  };

  static contextType = TooltipContext;

  constructor() {
    super();
    this.state = {
      contentHeight: 1,
      contentWidth: 1,
      offsetModifier: 0,
    };
  }

  componentDidMount() {
    this.setContentDimensions();
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (equal(this.props, nextProps) && equal(this.state, nextState)) {
      return false;
    }
    return true;
  }

  componentDidUpdate(previousProp) {
    if (!equal(this.props.x, previousProp.x)) {
      this.setContentDimensions();
    }
  }

  getContentLayout(contentHeight, contentWidth, padding, anchorLength) {
    return {
      height: contentHeight + padding * 2,
      width: contentWidth + padding * 2,
      x: -contentWidth / 2,
      y: -contentHeight - padding - anchorLength,
    };
  }

  getData(series, activeIndex) {
    return series
      .map((dataSeries) =>
        dataSeries.data.filter((datum, index) => index === activeIndex),
      )
      .reduce((prev, current) => prev.concat(current), []);
  }

  getDatum(data) {
    return data[0] || {};
  }

  getItemsY(timestamp) {
    return timestamp ? 28 : 0;
  }

  getOffsetModifier(contentWidth = 0) {
    const {padding} = this.props;
    const {offsetModifier} = this.state;
    const screenEdges = this.context.getContainerEdges();
    if (!screenEdges.left || !screenEdges.right) return 0;
    const getEdgeValue = (edgeName) =>
      screenEdges[edgeName] ? parseInt(screenEdges[edgeName], 10) : 0;
    const tooltipRect = this._tooltip.getBoundingClientRect();
    const tooltipWidth = contentWidth + 2 * padding;
    const widthDiff = (tooltipRect.width - tooltipWidth) * 0.5;
    const position = tooltipRect.left + offsetModifier * -1 + widthDiff;
    const rightEdge = getEdgeValue('right');
    const leftEdge = getEdgeValue('left');
    if (rightEdge <= position + tooltipWidth) {
      return rightEdge - (position + tooltipWidth);
    } else if (leftEdge > position) {
      return leftEdge - position;
    }
    return 0;
  }

  getTimestamp(date, dateFormatted) {
    if (dateFormatted) {
      return dateFormatted;
    }
    if (date) {
      return formatDate(date, DateTimeFormat.TEXTUAL_DAY_OF_WEEK);
    }
    return '';
  }

  getVisibility(datum) {
    return Object.keys(datum).length ? 'visible' : 'hidden';
  }

  setContentDimensions() {
    const {height, width} = this._content.getBBox();
    const state = {};
    if (
      (isValid(height) && height !== this.state.contentHeight) ||
      (isValid(width) && width !== this.state.contentWidth)
    ) {
      state.contentHeight = height;
      state.contentWidth = width;
    }
    if (this._tooltip) {
      state.offsetModifier = this.getOffsetModifier(width);
    }
    this.setState(state);
  }

  render() {
    const data = this.getData(this.props.series, this.props.activeIndex);
    const datum = this.getDatum(data);
    const timestamp = this.getTimestamp(datum.date, datum.dateFormatted);
    const contentLayout = this.getContentLayout(
      this.state.contentHeight,
      this.state.contentWidth,
      this.props.padding,
      this.props.anchorLength,
    );
    const itemsY = this.getItemsY(timestamp);
    const visibility = this.getVisibility(datum);
    return (
      <g
        className={styles.Tooltip}
        style={{
          visibility,
        }}
        transform={getTranslate(this.props.x, this.props.y)}>
        <TooltipBackground
          anchorLength={this.props.anchorLength}
          contentHeight={contentLayout.height}
          contentWidth={contentLayout.width}
          offsetModifier={this.state.offsetModifier}
          ref={(tooltip) => (this._tooltip = tooltip)}
          x={-contentLayout.width / 2}
          y={-contentLayout.height - this.props.anchorLength}
        />

        <g
          className={styles.TooltipContent}
          ref={(content) => (this._content = content)}
          transform={getTranslate(
            contentLayout.x + this.state.offsetModifier,
            contentLayout.y,
          )}>
          <SvgText
            style={{
              fill: '#fff',
              fontSize: '14px',
            }}
            text={timestamp}
          />

          <g
            className={styles.TooltipItems}
            transform={getTranslate(0, itemsY)}>
            {this.props.series.map((seriesDatum, index) => {
              const item = data[index] || {};
              return (
                <TooltipItem
                  color={item.color}
                  itemLabel={item.label}
                  key={`tooltipSeries${index}`}
                  legendLabel={truncate(seriesDatum.legendLabel)}
                  mask={seriesDatum.mask}
                  readout={item.readout}
                  style={seriesDatum.style}
                  type={seriesDatum.type}
                  y={this.props.itemHeight * index}
                />
              );
            })}
          </g>
        </g>
      </g>
    );
  }
}
